import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client/react";
import {
  Loader2,
  Flag,
  BookOpen,
  Check,
  Undo2,
  EyeOff,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
} from "lucide-react";

import { GET_FLAGGED_CONTENT } from "@/graphql/queries/admin";
import { RESOLVE_CONTENT_FLAG, COPY_FLAG_REPORT } from "@/graphql/mutations/admin";
import { CopyForAi } from "@/components/admin/copy-for-ai";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TheoryView } from "@/components/theory-view";
import { MathContent } from "@/components/math/math-content";
import { reportReasonLabel } from "@/lib/report-reasons";
import { toast } from "sonner";

type FlagKind = "EXERCISE" | "THEORY";
type FlagAction = "DISMISS" | "APPLY" | "REVERT";

type ExerciseFlag = {
  file_path: string;
  kind?: string;
  verdict?: string;
  question?: string;
  expected_answer?: string;
  student_answer?: string;
  explanation?: string;
  /** `student_report` only — see ExerciseFlagger::REPORT_REASONS. */
  report_reason?: string;
  report_comment?: string;
  student_saw?: string;
  suggested_json?: string | null;
  current_json?: string | null;
  has_original?: boolean;
  rewritten?: boolean;
  skill_key?: string;
  level?: number;
  student_name?: string;
  occurrences?: number;
  flagged_at?: string;
  pending?: boolean;
  /** When this was last handed to an AI from the copy button. Null means never. */
  copied_at?: string | null;
};

type TheoryCase = {
  question?: string;
  correct_answer?: string;
  student_answer?: string;
  retry_answer?: string;
  level?: number;
};

type TheoryFlag = {
  file_path: string;
  kind?: string;
  verdict?: string;
  skill_key?: string;
  skill_name?: string;
  subject?: string;
  grade?: number;
  /** What the tutor said the theory is missing. */
  missing?: string;
  /** What the tutor suggested adding, in prose. */
  suggestion?: string;
  current_html?: string | null;
  cases?: TheoryCase[];
  occurrences?: number;
  flagged_at?: string;
  copied_at?: string | null;
};

function formatDate(value?: string) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString("lt-LT");
}

/**
 * A question, rendered the way the child met it.
 *
 * This queue is read to decide whether a question is broken, and it used to
 * print the snapshot as text — so „Tiesėje pažymėti trys taškai $A$, $B$ ir $C$"
 * arrived as raw LaTeX and a figure question arrived with no figure. Neither is
 * something a grown-up can judge. `MathContent` is the same renderer the solve
 * page uses, so what is on this card is what was on the child's screen.
 *
 * **Figures are not tappable here** (`zoomableFigures` stays off), the rule the
 * admin browser follows: a figure sits among controls a click would compete
 * with. It is capped instead, because a full-width protractor pushes the flag's
 * own buttons off the card.
 */
function FlaggedText({ html, className }: { html?: string; className?: string }) {
  if (!html) return null;
  return (
    <MathContent
      html={html}
      className={`exercise-content [&_img]:max-w-[16rem] [&_img]:max-h-40 [&_p]:mb-1${
        className ? ` ${className}` : ""
      }`}
    />
  );
}

export default function FlaggedContentPage() {
  const { devTools } = useAuth();
  const { data, loading, refetch } = useQuery(GET_FLAGGED_CONTENT, {
    fetchPolicy: "cache-and-network",
  });
  const [resolveFlag, { loading: resolving }] = useMutation(RESOLVE_CONTENT_FLAG);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const exercises: ExerciseFlag[] = (data as any)?.flaggedContent?.exercises || [];
  const theory: TheoryFlag[] = (data as any)?.flaggedContent?.theory || [];

  function toggle(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function act(kind: FlagKind, filePath: string, action: FlagAction) {
    try {
      const { data: res } = await resolveFlag({ variables: { kind, filePath, action } });
      const payload = (res as any)?.resolveContentFlag;
      if (payload?.success) {
        toast.success(payload.message || "Atlikta");
        refetch();
      } else {
        toast.error(payload?.errors?.[0] || "Nepavyko");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nepavyko");
    }
  }

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Flag className="h-7 w-7 text-orange-500" />
          Turinys peržiūrai
        </h1>
        <p className="text-muted-foreground">
          Viena eilė visam, ką reikia perskaityti: mokinių pranešimai apie sugedusias
          užduotis, jų nesutikimai su vertinimu, užduotys, kurias AI palaikė netiksliomis,
          užduotys, kurių mokinys taip ir neįveikė, ir teorija, kurios nepakako.
        </p>
        </div>
        {/* The whole queue at once. Copying records the handover on every flag
            it covered — see CopyForAi — so the per-card buttons below can then
            say which of them have already gone. */}
        {(exercises.length > 0 || theory.length > 0) && (
          <CopyForAi
            document={COPY_FLAG_REPORT}
            pick={(data) => data?.copyFlagReport}
            variables={{}}
            label="Kopijuoti AI"
            hint="Visą eilę — įklijuok pokalbyje su AI"
            onCopied={() => refetch()}
          />
        )}
      </div>

      <Tabs defaultValue="exercises">
        <TabsList>
          <TabsTrigger value="exercises">
            Užduotys
            {exercises.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {exercises.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="theory">
            Teorija
            {theory.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {theory.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── Flagged exercises ─────────────────────────────────────────── */}
        <TabsContent value="exercises" className="space-y-4 mt-4">
          {exercises.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Peržiūrėti nėra ko — visos užduotys tvarkingos.
              </CardContent>
            </Card>
          ) : (
            exercises.map((flag) => {
              const key = `ex-${flag.file_path}`;
              const isExpanded = expanded.has(key);
              return (
                <Card key={key}>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        {/* A question is a sentence, not a heading: `font-semibold` and
                            `leading-none` come from CardTitle and cramp two lines of
                            maths into one another. */}
                        <CardTitle className="text-base font-medium leading-snug">
                          {flag.question ? (
                            <FlaggedText html={flag.question} />
                          ) : (
                            flag.file_path
                          )}
                        </CardTitle>
                        <CardDescription className="flex flex-wrap items-center gap-2 mt-1">
                          {flag.skill_key && <span>{flag.skill_key}</span>}
                          {flag.level && <span>· {flag.level} lygis</span>}
                          {flag.student_name && <span>· {flag.student_name}</span>}
                          {flag.flagged_at && <span>· {formatDate(flag.flagged_at)}</span>}
                          {(flag.occurrences || 0) > 1 && (
                            <Badge variant="outline">{flag.occurrences} kartai</Badge>
                          )}
                          {flag.pending && <Badge variant="outline">nepatvirtinta</Badge>}
                        </CardDescription>
                      </div>
                      <div className="flex flex-shrink-0 gap-1.5">
                        {flag.kind === "task_flawed" ? (
                          <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200">
                            Ydinga užduotis
                          </Badge>
                        ) : flag.kind === "student_recheck" ? (
                          <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200">
                            Mokinys nesutinka
                          </Badge>
                        ) : flag.kind === "student_report" ? (
                          <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200">
                            Mokinys pranešė
                          </Badge>
                        ) : flag.kind === "learner_stuck" ? (
                          /* Nobody reported this one — it was counted. The
                             learner met the question three times and got past
                             it none of them, which is either a question beyond
                             her or a broken one, and only a person can say
                             which. See StuckExercises. */
                          <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                            Mokinys įstrigo
                          </Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
                            Atsakymas priimtas
                          </Badge>
                        )}
                        {flag.rewritten && (
                          <Badge variant="outline" className="gap-1">
                            <Check className="h-3 w-3" />
                            jau pataisyta
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid gap-2 sm:grid-cols-2 text-sm">
                      <div className="rounded-md border p-2">
                        <div className="text-xs text-muted-foreground">Įrašytas atsakymas</div>
                        <div className="font-medium">
                          {flag.expected_answer ? (
                            <FlaggedText html={flag.expected_answer} />
                          ) : (
                            "—"
                          )}
                        </div>
                      </div>
                      <div className="rounded-md border p-2">
                        <div className="text-xs text-muted-foreground">Mokinio atsakymas</div>
                        {/* The child's own typing, rendered the same way: an
                            answer key reading `$\frac{1}{2}$` beside a raw one
                            is two different questions to compare. */}
                        <div className="font-medium">
                          {flag.student_answer ? (
                            <FlaggedText html={flag.student_answer} />
                          ) : (
                            "—"
                          )}
                        </div>
                      </div>
                    </div>

                    {/* A learner's own words about the question. Kept above the
                        AI's explanation and visually apart from it: this is the
                        one line in the queue a person wrote, and the reason chip
                        is what makes a pile of these sortable by eye. */}
                    {flag.kind === "student_report" && (
                      <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm dark:border-rose-900/50 dark:bg-rose-950/30">
                        <div className="font-medium text-rose-900 dark:text-rose-100">
                          {reportReasonLabel(flag.report_reason) || "Mokinio pranešimas"}
                        </div>
                        {flag.report_comment && (
                          <p className="mt-1 whitespace-pre-wrap text-rose-950/80 dark:text-rose-100/80">
                            „{flag.report_comment}"
                          </p>
                        )}
                        {/* In a personalized session the child read a different
                            story than the file holds, and "the wording is
                            confusing" is about the wording they actually met. */}
                        {flag.student_saw && flag.student_saw !== flag.question && (
                          <div className="mt-2 text-xs text-muted-foreground">
                            Mokinys matė:{" "}
                            <FlaggedText html={flag.student_saw} className="inline" />
                          </div>
                        )}
                      </div>
                    )}

                    {flag.explanation && flag.kind !== "student_report" && (
                      <div className="rounded-md bg-muted/60 p-3 text-sm">
                        <span className="font-medium">AI paaiškinimas: </span>
                        {flag.explanation}
                      </div>
                    )}

                    <div className="flex flex-wrap items-center gap-2">
                      {flag.suggested_json && (
                        <Button
                          size="sm"
                          disabled={resolving}
                          onClick={() => act("EXERCISE", flag.file_path, "APPLY")}
                        >
                          <Check className="h-4 w-4 mr-1" />
                          {flag.rewritten ? "Pritaikyti pasiūlymą dar kartą" : "Pritaikyti pasiūlymą"}
                        </Button>
                      )}
                      {flag.has_original && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={resolving}
                          onClick={() => act("EXERCISE", flag.file_path, "REVERT")}
                        >
                          <Undo2 className="h-4 w-4 mr-1" />
                          Atstatyti originalą
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={resolving}
                        onClick={() => act("EXERCISE", flag.file_path, "DISMISS")}
                      >
                        <EyeOff className="h-4 w-4 mr-1" />
                        Peržiūrėta
                      </Button>
                      {/* Hand this one question over. It stays in the queue —
                          copying is not reviewing, and the row is what says
                          whether the fix landed. The hover carries when it last
                          went. */}
                      <CopyForAi
                        document={COPY_FLAG_REPORT}
                        pick={(data) => data?.copyFlagReport}
                        variables={{ target: "exercise", filePath: flag.file_path }}
                        label="Kopijuoti AI"
                        hint="Šią užduotį su mokinio žodžiais ir JSON"
                        copiedAt={flag.copied_at}
                        variant="ghost"
                        size="xs"
                        onCopied={() => refetch()}
                      />
                      <button
                        type="button"
                        onClick={() => toggle(key)}
                        className="ml-auto inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground cursor-pointer"
                      >
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                        JSON
                      </button>
                    </div>

                    {isExpanded && (
                      <div className="grid gap-3 lg:grid-cols-2">
                        <div>
                          <div className="text-xs font-medium text-muted-foreground mb-1">
                            Dabartinis failas — {flag.file_path}
                          </div>
                          <pre className="max-h-80 overflow-auto rounded-md bg-muted p-3 text-xs">
                            {flag.current_json || "—"}
                          </pre>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-muted-foreground mb-1">
                            AI pasiūlymas
                          </div>
                          <pre className="max-h-80 overflow-auto rounded-md bg-muted p-3 text-xs">
                            {flag.suggested_json || "AI pasiūlymo nėra"}
                          </pre>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>

        {/* ── Flagged theory ────────────────────────────────────────────── */}
        <TabsContent value="theory" className="space-y-4 mt-4">
          {theory.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Teorijos, kurios nepakako, nėra.
              </CardContent>
            </Card>
          ) : (
            theory.map((flag) => {
              const key = `th-${flag.file_path}`;
              const isExpanded = expanded.has(key);
              const isMissing = flag.kind === "theory_missing";
              return (
                <Card key={key}>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <CardTitle className="text-base flex items-center gap-2">
                          <BookOpen className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          {flag.skill_name || flag.skill_key}
                        </CardTitle>
                        <CardDescription className="flex flex-wrap items-center gap-2 mt-1">
                          {flag.skill_key && <span>{flag.skill_key}</span>}
                          {flag.grade && <span>· {flag.grade} klasė</span>}
                          {flag.flagged_at && <span>· {formatDate(flag.flagged_at)}</span>}
                          {(flag.occurrences || 0) > 1 && (
                            <Badge variant="outline">{flag.occurrences} mokinių</Badge>
                          )}
                        </CardDescription>
                      </div>
                      {isMissing ? (
                        <Badge className="flex-shrink-0 gap-1 bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200">
                          <AlertTriangle className="h-3 w-3" />
                          Teorijos nėra
                        </Badge>
                      ) : (
                        <Badge className="flex-shrink-0 bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                          Per mažai teorijos
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {flag.missing && (
                      <div className="rounded-md bg-muted/60 p-3 text-sm">
                        <span className="font-medium">Trūksta: </span>
                        {flag.missing}
                      </div>
                    )}
                    {flag.suggestion && (
                      <div className="rounded-md border border-amber-200 bg-amber-50/70 dark:border-amber-900 dark:bg-amber-950/30 p-3 text-sm whitespace-pre-line">
                        <span className="font-medium">AI pasiūlymas: </span>
                        {flag.suggestion}
                      </div>
                    )}

                    {flag.cases && flag.cases.length > 0 && (
                      <div className="space-y-1.5">
                        <div className="text-xs font-medium text-muted-foreground">
                          Užduotys, kurių mokiniai neįveikė
                        </div>
                        {flag.cases.map((c, i) => (
                          <div key={i} className="rounded-md border p-2 text-sm">
                            <FlaggedText html={c.question} />
                            <div className="text-xs text-muted-foreground mt-1 flex flex-wrap items-center gap-1">
                              <span>teisingai:</span>
                              <FlaggedText html={c.correct_answer || "—"} className="inline" />
                              <span>· atsakė:</span>
                              <FlaggedText html={c.student_answer || "—"} className="inline" />
                              {c.retry_answer && (
                                <>
                                  <span>→</span>
                                  <FlaggedText html={c.retry_answer} className="inline" />
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex flex-wrap items-center gap-2">
                      {/* Only where the theory page exists: it is a development
                          screen (`DevTools`), and a button that bounces to the
                          dashboard is worse than no button. */}
                      {flag.skill_key && devTools && (
                        <Button size="sm" variant="outline" asChild>
                          <Link
                            // The preview names what is missing — which concept has
                            // no explanation, which has no example, which has no
                            // questions. Regenerating is a click further on.
                            to={`/admin/theory?skill=${encodeURIComponent(flag.skill_key)}&name=${encodeURIComponent(
                              flag.skill_name || flag.skill_key
                            )}&subject=${flag.subject || "math"}&grade=${flag.grade || 1}`}
                          >
                            <BookOpen className="h-4 w-4 mr-1" />
                            Tvarkyti teoriją
                          </Link>
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={resolving}
                        onClick={() => act("THEORY", flag.file_path, "DISMISS")}
                      >
                        <EyeOff className="h-4 w-4 mr-1" />
                        Peržiūrėta
                      </Button>
                      <CopyForAi
                        document={COPY_FLAG_REPORT}
                        pick={(data) => data?.copyFlagReport}
                        variables={{ target: "theory", filePath: flag.file_path }}
                        label="Kopijuoti AI"
                        hint="Šią teorijos spragą su neįveiktomis užduotimis"
                        copiedAt={flag.copied_at}
                        variant="ghost"
                        size="xs"
                        onCopied={() => refetch()}
                      />
                      <button
                        type="button"
                        onClick={() => toggle(key)}
                        className="ml-auto inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground cursor-pointer"
                      >
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                        Dabartinė teorija
                      </button>
                    </div>

                    {isExpanded && (
                      <div className="max-h-96 overflow-auto rounded-md border p-3">
                        {flag.current_html ? (
                          <TheoryView html={flag.current_html} className="text-sm" sourceOrder />
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            Teorijos failo nėra.
                          </span>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
